'use client';

import { useEffect, useState } from 'react';
import { nb } from '@/lib/nobreak';

/**
 * Appears once the hero leaves the viewport; the CTA is then never more than a thumb away.
 * With only `pitch` it is the compact one-line bar. With `eyebrow` and/or `body` (frontmatter `v2.sticky`) it becomes the
 * larger CTA card: icon tile, eyebrow, headline (the pitch), a body line and the button. All copy comes from the page.
 */
export function LPSticky({ href, label, pitch, eyebrow, body }: { href: string; label: string; pitch?: string; eyebrow?: string; body?: string }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const hero = document.getElementById('lp-hero');
    if (!hero) return;
    // Show the bar once the hero has scrolled away, but never on top of the page's own closing CTA or the footer
    // (owner, 2026-10-07: stacked CTA buttons near the bottom of the page): hide while any [data-lp-cta] block or the footer is on screen.
    const watch = [...Array.from(document.querySelectorAll('[data-lp-cta]')), document.querySelector('footer')].filter(Boolean) as Element[];
    const seen = new Set<Element>();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? seen.add(e.target) : seen.delete(e.target)));
      setShow(!seen.has(hero) && !watch.some((w) => seen.has(w)));
    }, { threshold: 0 });
    io.observe(hero);
    watch.forEach((w) => io.observe(w));
    return () => io.disconnect();
  }, []);
  const card = Boolean(eyebrow || body);
  const arrow = <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 10h11M11 5l5 5-5 5" strokeLinecap="round" /></svg>;
  return (
    <div
      aria-hidden={!show}
      className={`fixed inset-x-3 bottom-3 z-40 transition-all duration-300 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 ${card ? 'lp-sticky-card sm:w-[min(46rem,calc(100vw-2rem))]' : ''} ${show ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-6 opacity-0'}`}
    >
      {card ? (
        <div className="lp-sticky-in grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-3 rounded-[24px] bg-[#0B0C12]/95 p-4 text-white shadow-[0_20px_60px_-15px_rgba(0,0,0,.6)] ring-1 ring-white/10 backdrop-blur sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:p-5">
          <span aria-hidden className="lp-sticky-ico grid h-12 w-12 place-items-center rounded-[14px] bg-gradient-to-br from-[#ff34a7] to-[#294bfd] text-white">
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4.5" width="18" height="16" rx="3" /><path d="M8 2.5v4M16 2.5v4M3 10h18M9 15l2 2 4-4" /></svg>
          </span>
          <span className="lp-sticky-txt min-w-0">
            {eyebrow && <small className="block text-[11px] font-bold uppercase tracking-[.16em] text-[#ff7cc6]">{nb(eyebrow)}</small>}
            {pitch && <b className="lp-display-sm mt-0.5 block text-[1.05rem] font-semibold leading-tight sm:text-[1.2rem]">{pitch}</b>}
            {body && <span className="mt-1 hidden text-[13.5px] leading-snug text-white/70 sm:block">{nb(body)}</span>}
          </span>
          <a href={href} tabIndex={show ? 0 : -1} className="lp-shimmer col-span-2 inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#ff34a7] px-6 text-[15px] font-semibold text-white hover:bg-[#f41f9b] sm:col-span-1">
            <span>{nb(label)}</span>
            {arrow}
          </a>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-full sm:gap-4 bg-[#0B0C12]/95 py-2 pl-4 pr-2 text-white sm:pl-5 shadow-[0_20px_60px_-15px_rgba(0,0,0,.6)] ring-1 ring-white/10 backdrop-blur">
          {pitch && <span className="min-w-0 flex-1 text-balance text-[12px] font-medium leading-tight text-white/85 sm:flex-none sm:text-[14px] sm:font-normal sm:text-white/80">{pitch}</span>}
          <a href={href} tabIndex={show ? 0 : -1} className="lp-shimmer ml-auto inline-flex h-11 shrink-0 items-center gap-2 rounded-full bg-[#ff34a7] px-4 text-[13px] sm:px-5 sm:text-[14px] font-semibold text-white hover:bg-[#f41f9b] sm:ml-0">
            <span>{nb(label)}</span>
            {arrow}
          </a>
        </div>
      )}
    </div>
  );
}
